import { useCallback, useEffect, useRef } from 'react';
import { getCookie } from '@/core/utils/cookieUtils';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import type { ProductCustomization } from '@/features/customization/domain/models/ProductCustomization';
import type { ProductDetail } from '../../domain/models/ProductDetail';
import type { PdpEffectiveProductVm } from '../viewModels/pdp.vm';
import { toPdpEffectiveProductVm } from '../viewModels/pdp.vm';
import {
  buildPdpProductAttributes,
  getFirstMediaImageUrl,
  resolvePdpSellingPrice,
} from '../analytics/pdpProductAttributes';
import { buildFacebookPdpAttributes } from '../analytics/pdpFacebookAttributes';
import {
  buildProductViewedEventLoggerPayload,
  clearPdpGtmViewContentGlobals,
  formatEventLoggerSize,
  formatViewContentPrice,
  setPdpGtmViewContentGlobals,
} from '../analytics/pdpViewContent';
import type { PdpDeliveryStoreItemVm } from '../viewModels/pdpDelivery.vm';

export interface UsePdpAnalyticsParams {
  product: ProductDetail | null;
  vm: PdpEffectiveProductVm | null;
  selectedSizeDisplay?: string;
  /** Required for server-side product_viewed (FB ViewContent via eventlogger). */
  visitorId?: string | null;
  /** Wait for the latest `/user-context` response before firing ViewContent. */
  isUserContextReady?: boolean;
}

export interface PdpPincodeAnalyticsExtras {
  pinErrorFlag?: boolean;
  estimatedDeliveryDate?: string | Date | null;
  deliveryTime?: number | null;
  broadCity?: string | null;
  nearbyStoreAvailable?: boolean | null;
}

export interface PdpStoreSelectedAnalytics {
  storeName: string;
  city?: string;
  storeIds?: string[];
}

export function getDeliveryDaysDifference(eddFullDate: string | Date | null | undefined): number | null {
  if (!eddFullDate) return null;
  const deliveryDate = eddFullDate instanceof Date ? eddFullDate : new Date(eddFullDate);
  if (Number.isNaN(deliveryDate.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  deliveryDate.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((deliveryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
}

function buildPdpTrackAttributes(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
  selectedSizeDisplay?: string,
): Record<string, unknown> {
  return {
    ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
    ...buildFacebookPdpAttributes(product, vm, selectedSizeDisplay),
  };
}

function customizationChangeSignature(vm: PdpEffectiveProductVm): string {
  return [
    vm.activeCustomizationId,
    vm.metal,
    vm.goldPurity,
    vm.diamondStoneType ?? '',
    vm.solitaireCarat ?? '',
  ].join('|');
}

function buildPincodeEventAttributes(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
  selectedSizeDisplay: string | undefined,
  pincode: string,
  extras?: PdpPincodeAnalyticsExtras,
): Record<string, unknown> {
  const attrs: Record<string, unknown> = {
    ...buildPdpTrackAttributes(product, vm, selectedSizeDisplay),
    Pincode: pincode,
  };

  if (extras?.pinErrorFlag !== undefined) {
    attrs['Error Flag'] = extras.pinErrorFlag;
  }
  if (extras?.estimatedDeliveryDate) {
    attrs['Estimated Delivery Date'] =
      extras.estimatedDeliveryDate instanceof Date
        ? extras.estimatedDeliveryDate
        : new Date(extras.estimatedDeliveryDate);
  }
  if (extras?.deliveryTime != null) {
    attrs['Delivery Time'] = extras.deliveryTime;
  }
  if (extras?.broadCity) {
    attrs.broad_city = extras.broadCity;
  }
  if (extras?.nearbyStoreAvailable != null) {
    attrs.nearby_store_available = extras.nearbyStoreAvailable ? 'true' : 'false';
  }

  return attrs;
}

export function usePdpAnalytics({
  product,
  vm,
  selectedSizeDisplay,
  visitorId,
  isUserContextReady = false,
}: UsePdpAnalyticsParams) {
  const { analytics, isInitialized } = useAnalytics();
  const serverViewContentFiredDesignIdRef = useRef<number | null>(null);
  const fbViewContentFiredDesignIdRef = useRef<number | null>(null);
  const lastDataLayerDesignIdRef = useRef<number | null>(null);
  const pdpPageViewedDesignIdRef = useRef<number | null>(null);
  const customizationBaselineRef = useRef<string | null>(null);
  const playedVideoSlideKeysRef = useRef<Set<string>>(new Set());

  // GTM remarketing globals + dataLayer signal (legacy PDP parity).
  // Meta ViewContent / OpenAI contents_viewed are mapped from `PDP Page Viewed` by the adapters.
  useEffect(() => {
    if (!product || !vm) return;

    setPdpGtmViewContentGlobals(product, resolvePdpSellingPrice(vm, product));

    if (lastDataLayerDesignIdRef.current === product.designId) {
      return;
    }

    lastDataLayerDesignIdRef.current = product.designId;
    const dataLayer = typeof window !== 'undefined'
      ? (window as unknown as { dataLayer?: unknown[] }).dataLayer
      : undefined;
    if (Array.isArray(dataLayer)) {
      dataLayer.push({ event: 'React Product page loaded' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.designId, vm?.discountedPrice, vm?.price]);

  useEffect(() => {
    if (!product || !vm) return;
    if (!isUserContextReady) return;
    if (fbViewContentFiredDesignIdRef.current === product.designId) return;
    fbViewContentFiredDesignIdRef.current = product.designId;
    customizationBaselineRef.current = customizationChangeSignature(vm);
    playedVideoSlideKeysRef.current = new Set();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.designId, vm?.activeCustomizationId, isUserContextReady]);

  // ── On mount / product change: client page-view events ────────────────────
  useEffect(() => {
    if (!analytics || !isInitialized || !product || !vm) return;
    // Don't lock the designId until a price exists; incomplete SSR otherwise
    // sends Selling Price as null/0 and never retries.
    if (
      vm.discountedPrice == null &&
      vm.price == null &&
      product.discountedPrice == null &&
      product.price == null
    ) {
      return;
    }
    if (pdpPageViewedDesignIdRef.current === product.designId) return;

    pdpPageViewedDesignIdRef.current = product.designId;
    const attrs = buildPdpTrackAttributes(product, vm, selectedSizeDisplay);
    // Kept as 'PDP Page Viewed' — WebEngage campaigns are wired to this exact event name.
    // TODO: once a dedicated WebEngage event-name mapper exists, revisit unifying this
    // with the `ViewContent` name used by the Meta/OpenAI ad pixels.
    analytics.trackEvent('PDP Page Viewed', attrs);

    analytics.logGoKwikSystemEvent('product', {
      product_id: product.designId,
      variant_id: vm.activeCustomizationId,
      image_url: getFirstMediaImageUrl(product.mediaItems),
      name: product.designName ?? '',
      price: resolvePdpSellingPrice(vm, product),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, isInitialized, product?.designId, vm?.discountedPrice, vm?.price]);

  // Legacy: product_viewed + browsingHistory fire once per PDP when visitorId exists.
  // Kept separate from the client page-view lock so a missing visitorId on first
  // render does not permanently skip the eventLogger POSTs.
  useEffect(() => {
    if (!analytics || !isInitialized || !product || !vm) return;
    if (!isUserContextReady) return;
    if (visitorId == null || visitorId === '') return;
    if (serverViewContentFiredDesignIdRef.current === product.designId) return;

    const weLuid = getCookie('we_luid');
    const productViewedPayload = buildProductViewedEventLoggerPayload(product, vm);
    if (weLuid) {
      productViewedPayload.we_luid = weLuid;
    }

    analytics.logEventLogger(productViewedPayload);

    analytics.logEventLogger({
      _eventId: 'browsingHistory',
      id: vm.activeCustomizationId,
      designId: product.designId,
      pageType: 'product',
      size: formatEventLoggerSize(product.size),
      skuCode: product.skuCode ?? '',
      product_category: product.category ?? '',
      collectionName: product.collectionName ?? '',
      productName: product.designName ?? '',
      price: formatViewContentPrice(resolvePdpSellingPrice(vm, product)),
    });

    serverViewContentFiredDesignIdRef.current = product.designId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, isInitialized, product?.designId, visitorId, isUserContextReady]);

  useEffect(() => {
    return () => {
      clearPdpGtmViewContentGlobals();
    };
  }, []);

  // ── Scroll ─────────────────────────────────────────────────────────────────

  const trackMaxScrollView = useCallback(
    (scrollPercent: number) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent('Max Scroll View', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        'Scroll Percent': scrollPercent,
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  // ── Buy actions ────────────────────────────────────────────────────────────

  const trackBuyNow = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Buy Now Clicked', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackAddToCart = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Added To Cart', buildPdpTrackAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackWishlistToggle = useCallback(
    (action: 'add' | 'remove') => {
      if (!analytics || !product || !vm) return;
      const eventName = action === 'add' ? 'Added To Wishlist' : 'Removed From Wishlist';
      analytics.trackEvent(eventName, buildPdpTrackAttributes(product, vm, selectedSizeDisplay));
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  // ── GMS (Gold Mine Savings) ────────────────────────────────────────────────

  const trackGmsPlanClick = useCallback(
    (monthlyAmount: number) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent('GMS Plan Clicked', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        'Monthly Amount': monthlyAmount,
        'Discount Name': '',
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackGmsSubscribeClick = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('GMS Subscribe Clicked', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackGmsTncClick = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('GMS T&C Clicked', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const maybeFireCustomizeProduct = useCallback(
    (effectiveVm: PdpEffectiveProductVm) => {
      if (!analytics || !product) return;
      const signature = customizationChangeSignature(effectiveVm);
      if (customizationBaselineRef.current === null) {
        customizationBaselineRef.current = signature;
        return;
      }
      if (customizationBaselineRef.current === signature) return;
      customizationBaselineRef.current = signature;
      analytics.trackEvent(
        'CustomizeProduct',
        buildPdpTrackAttributes(product, effectiveVm, selectedSizeDisplay),
        { eventProviders: ['facebook', 'openai'] },
      );
    },
    [analytics, product, selectedSizeDisplay],
  );

  // ── Size & sizer ───────────────────────────────────────────────────────────

  const trackSizeSelected = useCallback(
    (sizeDisplay: string) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent('Size Selected', buildPdpProductAttributes(product, vm, sizeDisplay));
    },
    [analytics, product, vm],
  );

  const trackFreeSizerClick = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Free Sizer Clicked', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackFreeSizerDetailsAdded = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Free Sizer Details Added', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackResendOtp = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Resend OTP', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackOtpVerified = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('OTP Verified', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  // ── Customization ──────────────────────────────────────────────────────────

  const trackCustomizationSelected = useCallback(
    (customizationType: string, appliedCustomization?: ProductCustomization) => {
      if (!analytics || !product || !vm) return;
      const effectiveVm = appliedCustomization
        ? toPdpEffectiveProductVm(product, appliedCustomization)
        : vm;
      analytics.trackEvent('Customization Selected', {
        ...buildPdpProductAttributes(product, effectiveVm, selectedSizeDisplay),
        'Customization Type': customizationType,
      });
      maybeFireCustomizeProduct(effectiveVm);
    },
    [analytics, product, vm, selectedSizeDisplay, maybeFireCustomizeProduct],
  );

  const trackCustomizationClick = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Customization Clicked', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  // ── Delivery / pincode ─────────────────────────────────────────────────────

  const trackPincodeEntered = useCallback(
    (pincode: string, extras?: PdpPincodeAnalyticsExtras) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent(
        'Pincode Entered',
        buildPincodeEventAttributes(product, vm, selectedSizeDisplay, pincode, extras),
      );
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackPincodeChecked = useCallback(
    (pincode: string, extras?: PdpPincodeAnalyticsExtras) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent(
        'Pincode Checked',
        buildPincodeEventAttributes(product, vm, selectedSizeDisplay, pincode, extras),
      );
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackDesignsInStoresClick = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Designs in Stores Clicked', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackAvailableStoresClick = useCallback(
    (stores?: PdpDeliveryStoreItemVm[]) => {
      if (!analytics || !product || !vm) return;
      const firstStore = stores?.[0];
      analytics.trackEvent('Available Stores Clicked', {
        ...buildPdpTrackAttributes(product, vm, selectedSizeDisplay),
        ...(firstStore
          ? {
              store_id: firstStore.storeId,
              store_name: firstStore.storeName,
              city: firstStore.cityName,
              result_count: String(stores?.length ?? 0),
              source_page: 'pdp',
            }
          : {}),
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackStoreSelected = useCallback(
    ({ storeName, city, storeIds }: PdpStoreSelectedAnalytics) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent('Store Selected', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        'Store Name': storeName,
        ...(city ? { City: city } : {}),
        ...(storeIds?.length ? { 'Store ID': storeIds.join(',') } : {}),
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  // ── Product info / details ─────────────────────────────────────────────────

  const trackProductDetailsInfoIconViewed = useCallback(
    (buttonName: string) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent('Product Details Info Icon Viewed', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        'Hovered Button Name': buttonName,
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackProductDetailsViewed = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Product Details Viewed', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackViewMoreDetailsClick = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('View More Details Clicked', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackDiscountTncClick = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Discount T&C Clicked', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackCertificationClick = useCallback(
    (certificationName: string) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent('Certification Clicked', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        'Certification Name': certificationName,
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackKnowMoreClick = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Know More Clicked', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  // ── Media / gallery ────────────────────────────────────────────────────────

  const trackMediaThumbnailClick = useCallback(
    (mediaIndex: number) => {
      if (!analytics || !product || !vm) return;
      const carouselSeq = (product.mediaItems as { carouselSeq?: Array<{ type?: string; urlMap?: Record<string, string> }> })
        .carouselSeq;
      const slide = carouselSeq?.[mediaIndex];
      const mediaType = slide?.type === 'video' ? 'video' : 'image';
      let mediaUrl = '';
      if (slide?.type === 'image' && slide.urlMap) {
        mediaUrl = slide.urlMap['1024'] ?? slide.urlMap['70'] ?? '';
      } else if (slide?.type === 'video') {
        mediaUrl = 'video placeholder image';
      }
      analytics.trackEvent('Product Media Thumbnail Clicked', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        Position: mediaIndex + 1,
        'Media Type': mediaType,
        'Media URL': mediaUrl,
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackMediaThumbnailScrolled = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Product Media Thumbnail Scrolled', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  const trackPdpVideoPlayed = useCallback(
    (mediaIndex: number) => {
      if (!analytics || !product || !vm) return;
      const carouselSeq = (product.mediaItems as { carouselSeq?: Array<{ type?: string }> }).carouselSeq;
      const slide = carouselSeq?.[mediaIndex];
      if (slide?.type !== 'video') return;

      const slideKey = `${product.designId}:${mediaIndex}`;
      if (playedVideoSlideKeysRef.current.has(slideKey)) return;
      playedVideoSlideKeysRef.current.add(slideKey);
      analytics.trackEvent(
        'PDP Video Played',
        buildPdpTrackAttributes(product, vm, selectedSizeDisplay),
        { eventProviders: ['facebook'] },
      );
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackMediaViewed = useCallback(
    (mediaIndex: number) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent('Product Media Viewed', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        Position: mediaIndex + 1,
        'Media Index': mediaIndex,
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackProductShareClick = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent(
      'Clicked WhatsApp Share on Product',
      buildPdpProductAttributes(product, vm, selectedSizeDisplay),
      { eventProviders: ['ga'] },
    );
  }, [analytics, product, vm, selectedSizeDisplay]);

  // ── Contact ────────────────────────────────────────────────────────────────

  const trackContactButtonClick = useCallback(
    (contactType: 'Call' | 'Chat') => {
      if (!analytics || !product || !vm) return;
      // Server-side eventLogger event for chat
      if (contactType === 'Chat') {
        analytics.logEventLogger({
          _eventId: 'mw_pdp_contactus_clicked',
          product_name: product.designName ?? '',
          category: product.category ?? '',
          button_clicked: 'chat',
        });
      }
      analytics.trackEvent('Contact Button Clicked', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        'Contact Type': contactType,
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  // ── Cross-sell / related designs ───────────────────────────────────────────

  const trackOtherDesignSelected = useCallback(
    (ctaName: string) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent('Other Designs Selected', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        'CTA Name': ctaName,
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackFrequentlyBoughtItemSelected = useCallback(
    (fbtDesignId: number, fbtDesignName: string) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent('Frequently Bought Items Selected', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        'FBT Design Id': fbtDesignId,
        'FBT Design Name': fbtDesignName,
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  const trackSimilarDesignSelected = useCallback(
    (similarDesignId: number, similarDesignName: string) => {
      if (!analytics || !product || !vm) return;
      analytics.trackEvent('Similar Designs Selected', {
        ...buildPdpProductAttributes(product, vm, selectedSizeDisplay),
        'Similar Design Id': similarDesignId,
        'Similar Design Name': similarDesignName,
      });
    },
    [analytics, product, vm, selectedSizeDisplay],
  );

  // ── Video call cart ────────────────────────────────────────────────────────

  const trackVideoCallCartAdd = useCallback(() => {
    if (!analytics || !product || !vm) return;
    analytics.trackEvent('Added to Video Call Cart', buildPdpProductAttributes(product, vm, selectedSizeDisplay));
  }, [analytics, product, vm, selectedSizeDisplay]);

  return {
    isReady: !!analytics,
    trackMaxScrollView,
    trackBuyNow,
    trackAddToCart,
    trackWishlistToggle,
    trackGmsPlanClick,
    trackGmsSubscribeClick,
    trackGmsTncClick,
    trackSizeSelected,
    trackFreeSizerClick,
    trackFreeSizerDetailsAdded,
    trackResendOtp,
    trackOtpVerified,
    trackCustomizationSelected,
    trackCustomizationClick,
    trackPincodeEntered,
    trackPincodeChecked,
    trackDesignsInStoresClick,
    trackAvailableStoresClick,
    trackStoreSelected,
    trackProductDetailsInfoIconViewed,
    trackProductDetailsViewed,
    trackViewMoreDetailsClick,
    trackDiscountTncClick,
    trackCertificationClick,
    trackKnowMoreClick,
    trackMediaThumbnailClick,
    trackMediaThumbnailScrolled,
    trackMediaViewed,
    trackPdpVideoPlayed,
    trackProductShareClick,
    trackContactButtonClick,
    trackOtherDesignSelected,
    trackFrequentlyBoughtItemSelected,
    trackSimilarDesignSelected,
    trackVideoCallCartAdd,
  };
}
